import { useEffect, useState, useRef, useCallback } from "react"
import cn from "classnames"
import Arrow from "public/assets/down-arrow-blue-background.svg"
import Link from "next/link"
import useWindowSize from "utilities/useWindowSize"

export default function CategoryArchiveSection({ categories, archiveLink, categoryLinkPrefix, currentTab, list }) {
  const [open, setOpen] = useState(false)

  const [scrollButtonVisible, setScrollButtonVisible] = useState(false)
  const [navbarScrollPosition, setNavbarScrollPosition] = useState(0)

  const scrollRef = useRef(null)

  const { width } = useWindowSize()

  useEffect(() => {
    if (width >= 1024) {
      const maxScroll = scrollRef.current.scrollWidth - scrollRef.current.clientWidth

      if (maxScroll > 0 && navbarScrollPosition <= maxScroll / 2) {
        setScrollButtonVisible(true)
      } else {
        setScrollButtonVisible(false)
      }
    } else {
      setScrollButtonVisible(false)
    }
  }, [width, navbarScrollPosition])

  const Tab = useCallback(
    ({ name, value, link }) => (
      <Link
        className={cn(
          "font-primary lg:text-white transition-all duration-300 text-left mr-8 lg:mr-0 whitespace-nowrap py-2 lg:py-4 lg:decoration-tertiary lg:underline-offset-4 lg:hover:underline lg:decoration-2 capitalize",
          {
            "h-0 opacity-0 lg:py-4 lg:h-auto lg:opacity-100": currentTab.value !== value && !open,
            "lg:decoration-tertiary lg:underline": currentTab.value === value,
            "h-auto opacity-100": open,
          },
        )}
        href={link}
        onClick={() => setOpen(false)}
      >
        {name}
      </Link>
    ),
    [currentTab.value, open],
  )

  return (
    <div data-pagefind-ignore="all">
      <nav
        className={cn("bg-grey-cool-tint lg:bg-primary w-full lg:px-5 sticky lg:relative top-0 z-30", {
          hidden: archiveLink === "/team-members",
        })}
      >
        <div className="lg:max-w-screen-xl mx-auto flex flex-col lg:flex-row justify-between ">
          <button
            className={cn("sticky -left-1 mb-[-2px] z-20 hidden", {
              "lg:block": true,
            })}
            aria-label="Scroll Left"
            style={{ opacity: navbarScrollPosition / 45 }}
            onClick={() => {
              scrollRef.current.scroll({
                left: scrollRef.current.clientWidth - scrollRef.current.scrollWidth,
                behavior: "smooth",
              })
            }}
          >
            <Arrow className="h-14 lg:rotate-90" />
          </button>
          <button
            className={cn("self-start py-2 px-5 lg:hidden w-full text-left capitalize", {
              "scale-y-0": open,
              "scale-y-100": !open,
            })}
            onClick={() => setOpen(!open)}
          >
            {currentTab.name}
          </button>
          <div
            ref={scrollRef}
            className={cn(
              "archive-tab-bar flex flex-col lg:flex-row lg:gap-8 xl:gap-12 mx-auto bg-grey-cool-tint lg:bg-primary absolute lg:relative w-full transition-all duration-300 origin-top px-5 lg:px-0 z-10 lg:w-max lg:overflow-x-auto",
              {
                "scale-y-0 lg:scale-y-100": !open,
                "scale-y-100": open,
              },
            )}
            onScroll={(e) => {
              setNavbarScrollPosition(e.target.scrollLeft)
            }}
          >
            <Tab name="All" value="all" link={archiveLink} />
            {categories?.map((category) => (
              <Tab {...category} key={category.value} link={`${categoryLinkPrefix}${category.value}`} />
            ))}
            <button
              className={cn("sticky -right-1 z-20 hidden", {
                "lg:block": scrollButtonVisible,
              })}
              aria-label="Scroll Right"
              style={{ opacity: 1 - navbarScrollPosition / 45 }}
              onClick={() => {
                scrollRef.current.scroll({
                  left: scrollRef.current.scrollWidth - scrollRef.current.clientWidth,
                  behavior: "smooth",
                })
              }}
            >
              <Arrow className="h-14 lg:-rotate-90" />
            </button>
          </div>
          <button
            className="absolute right-0 top-0 z-20 lg:hidden"
            onClick={() => setOpen(!open)}
            title="Filter Archive items by Title"
          >
            <Arrow
              className={cn("h-10", {
                "rotate-180 lg:-rotate-90": open,
                "lg:-rotate-90": !open,
              })}
            />
          </button>
        </div>
      </nav>
      {list}
    </div>
  )
}
